import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Lock, Globe2, Building2, FolderKanban, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Separator } from '@/shared/components/ui/separator';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/core/lib/utils';
import { useToast } from '@/shared/hooks/useToast';
import { useProjects } from '@/shared/hooks/useProjects';
import { useFormSharing } from '../hooks/useFormSharing';
import { useShareForm } from '../hooks/useFormSharingMutations';
import { RecipientPicker } from './RecipientPicker';
import type { AudienceType, Recipient } from '../types';

interface ShareFormDialogProps {
  formId: string;
  /** Form's own project scope — gates the `Project` audience option and its hint. */
  projectId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AUDIENCE_OPTIONS: { value: AudienceType; icon: typeof Lock }[] = [
  { value: 'Private', icon: Lock },
  { value: 'Public', icon: Globe2 },
  { value: 'Organization', icon: Building2 },
  { value: 'Project', icon: FolderKanban },
  { value: 'Specific', icon: Users },
];

function CopyableLink({ id, value }: { id: string; value: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex gap-2">
      <Input id={id} data-testid={id} readOnly value={value} />
      <Button
        type="button"
        variant="outline"
        onClick={handleCopy}
        data-testid={`${id}-copy-button`}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
}

export function ShareFormDialog({ formId, projectId, open, onOpenChange }: ShareFormDialogProps) {
  const { t } = useTranslation('forms');
  const { orgId } = useParams<{ orgId: string }>();
  const { toast } = useToast();
  const { sharing, isLoading } = useFormSharing(formId, open);
  const shareForm = useShareForm(formId);
  const { data: projects = [] } = useProjects(open);

  const projectName = projectId ? projects.find((p) => p.id === projectId)?.name : undefined;
  const audienceOptions = AUDIENCE_OPTIONS.filter((o) => o.value !== 'Project' || !!projectId);

  const [audienceType, setAudienceType] = useState<AudienceType>('Private');
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [expiresAt, setExpiresAt] = useState('');

  const [syncedSharingKey, setSyncedSharingKey] = useState<{
    open: boolean;
    sharing: typeof sharing;
  }>({
    open,
    sharing,
  });
  if (open !== syncedSharingKey.open || sharing !== syncedSharingKey.sharing) {
    setSyncedSharingKey({ open, sharing });
    if (sharing && open) {
      setAudienceType(sharing.audienceType);
      setRecipients(sharing.recipients);
      setExpiresAt(sharing.expiresAt ?? '');
    }
  }

  const internalLink = `${window.location.origin}/${orgId}/forms/${formId}/respond`;
  const formLink =
    audienceType === 'Public' && sharing?.publicLink ? sharing.publicLink : internalLink;
  const isSaving = shareForm.isPending;

  function buildPayload() {
    return {
      audienceType,
      recipients: recipients.map((r) => ({ userId: r.userId, email: r.email, type: r.type })),
      expiresAt: expiresAt || null,
    };
  }

  async function handleShare() {
    try {
      const { data: result } = await shareForm.mutateAsync(buildPayload());
      const notified = result.emailsSent + result.inAppNotificationsSent;
      toast.success({
        title: t('share.toast.shared'),
        description: notified > 0 ? t('share.toast.notifiedCount', { count: notified }) : undefined,
      });
      onOpenChange(false);
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full h-full max-w-none rounded-none overflow-y-auto md:h-auto md:max-h-[85vh] md:max-w-lg md:rounded-lg"
        data-testid="forms-share-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('share.title')}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="forms-share-link-input">{t('share.linkLabel')}</Label>
              <CopyableLink id="forms-share-link-input" value={formLink} />
              <p className="text-xs text-muted-foreground">
                {audienceType === 'Public'
                  ? t('share.publicLinkHint')
                  : t('share.internalLinkHint')}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>{t('share.audience.label')}</Label>
              <div className="flex flex-wrap gap-2">
                {audienceOptions.map(({ value, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setAudienceType(value)}
                    className={cn(
                      'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors',
                      audienceType === value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:bg-accent',
                    )}
                    data-testid={`forms-share-audience-pill-${value}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {t(`share.audience.options.${value}`)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {audienceType === 'Project' && projectName
                  ? t('share.audience.hints.ProjectWithName', { project: projectName })
                  : t(`share.audience.hints.${audienceType}`)}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="forms-share-expiration-input">{t('share.expiration.label')}</Label>
              <Input
                id="forms-share-expiration-input"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                data-testid="forms-share-expiration-input"
              />
              <p className="text-xs text-muted-foreground">{t('share.expiration.hint')}</p>
            </div>

            {audienceType === 'Specific' && (
              <>
                <Separator />
                <div className="space-y-1.5">
                  <Label>{t('share.recipients.title')}</Label>
                  <RecipientPicker recipients={recipients} onChange={setRecipients} />
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
            data-testid="forms-share-cancel-button"
          >
            {t('share.actions.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleShare}
            disabled={isSaving || isLoading}
            data-testid="forms-share-share-button"
          >
            {t('share.actions.share')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
